import React, { useCallback, useEffect, useMemo, useState } from "react";
import Toast from "../../components/Toast/Toast";
import Sidebar from "../Sidebar/Sidebar";
import ProductSearchSelect from "../Videos/components/ProductSearchSelect";
import { getAllUsersAPI, sendBroadcastNotificationAPI } from "../../utils/api";
import { getErrorMessage } from "../../constants/globalFunction";
import "../WholesaleUsers/WholesaleUsers.css";
import "./BroadcastNotification.css";

const FETCH_PAGE_LIMIT = 100;

function productFilterId(selectedProduct) {
  if (!selectedProduct?._id) return "";
  return String(selectedProduct._id);
}

function idKey(user) {
  const id = user?._id;
  if (id == null) return "";
  return typeof id === "object" ? JSON.stringify(id) : String(id);
}

function userMatchesProduct(user, productId) {
  if (!productId) return true;
  const pid = String(productId);
  const direct = [
    user?.product_id,
    user?.productId,
    user?.product?._id,
    typeof user?.product === "string" ? user.product : null,
  ]
    .filter((x) => x != null && x !== "")
    .map((x) => String(typeof x === "object" ? x._id ?? x : x));
  if (direct.length > 0) return direct.includes(pid);
  const arr = user?.products ?? user?.product_ids;
  if (Array.isArray(arr)) {
    return arr.some((x) => String(x?._id ?? x) === pid);
  }
  return true;
}

async function fetchAllByUserType(userType) {
  let page = 1;
  const all = [];
  for (;;) {
    const response = await getAllUsersAPI({
      user_type: userType,
      page,
      limit: FETCH_PAGE_LIMIT,
      search: "",
      active:1,
      is_report:1,
 
    });
    if (response?.status !== 200 || !Array.isArray(response.data)) break;
    all.push(...response.data);
    const totalPages = Number(response?.pagination?.total_pages) || 1;
    if (page >= totalPages || response.data.length < FETCH_PAGE_LIMIT) break;
    page += 1;
    if (page > 500) break;
  }
  return all;
}

/**
 * Map a user record to an id for POST /admin/broadcast/notification/add (`users` array).
 * Prefers numeric ids when the API uses integers; otherwise keeps string ids.
 */
function toApiUserId(user) {
  if (typeof user?.id === "number" && Number.isFinite(user.id)) return user.id;
  if (typeof user?.user_id === "number" && Number.isFinite(user.user_id)) return user.user_id;
  const raw = user?.id ?? user?.userId ?? user?.user_id ?? user?._id;
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) return Number(s);
  return s;
}

/**
 * @param {{
 *   title: string;
 *   message: string;
 *   users: unknown[];
 *   product_id: string;
 * }} p
 */
function buildBroadcastPayload(p) {
  const out = {
    title: p.title,
    message: p.message,
    users: p.users,
  };
  if (p.product_id) out.product_id = p.product_id;
  return out;
}

/** Treat 200 / "200" / success flag as OK; message may live on `data`. */
function isBroadcastSendSuccess(res) {
  if (!res || typeof res !== "object") return false;
  const st = res.status;
  if (st === 200 || st === "200") return true;
  if (Number(st) === 200) return true;
  const inner = res.data;
  if (inner && typeof inner === "object") {
    const ist = inner.status;
    if (ist === 200 || ist === "200" || Number(ist) === 200) return true;
  }
  if (res.success === true || res.success === 1 || res.success === "1") return true;
  return false;
}

/** Toast text: prefer the API `message` (and common nested shapes). */
function getBroadcastSuccessToastMessage(res) {
  if (!res || typeof res !== "object") {
    return "Broadcast notification sent successfully.";
  }
  const from = [
    res.message,
    res.msg,
    typeof res.data === "object" ? res.data?.message : null,
    typeof res.data === "string" ? res.data : null,
  ];
  for (const c of from) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "Broadcast notification sent successfully.";
}

/**
 * Build `users` for the broadcast API: only checked users (wholesale + retail),
 * deduped. IDs come from the user record (`id`, `user_id`, `_id`, etc.).
 */
function collectUsersForBroadcast(p) {
  const {
    includeWholesaler,
    includeRetailer,
    productId,
    wholesaleUsers,
    retailUsers,
    selectedWholesale,
    selectedRetail,
  } = p;

  const pickSelected = (list, selectedSet) =>
    list
      .filter((u) => userMatchesProduct(u, productId))
      .filter((u) => idKey(u) && selectedSet.has(idKey(u)));

  let combined = [];
  if (includeWholesaler) {
    combined = combined.concat(pickSelected(wholesaleUsers, selectedWholesale));
  }
  if (includeRetailer) {
    combined = combined.concat(pickSelected(retailUsers, selectedRetail));
  }

  const ids = [];
  const seen = new Set();
  for (const u of combined) {
    const apiId = toApiUserId(u);
    if (apiId == null) continue;
    const k = String(apiId);
    if (seen.has(k)) continue;
    seen.add(k);
    ids.push(apiId);
  }
  return ids;
}

function NameGrid({ users, selectedSet, onToggle, emptyMessage }) {
  return (
    <div className="broadcast-name-picker">
      {users.length === 0 ? (
        <div className="broadcast-empty broadcast-empty--inline">
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <div className="broadcast-name-grid-scroll">
          <div className="broadcast-name-grid" role="list">
            {users.map((user, idx) => {
              const key = idKey(user);
              const name = user?.name?.trim() || "—";
              return (
                <label key={key || `u-${idx}`} className="broadcast-name-cell">
                  <input
                    type="checkbox"
                    checked={key ? selectedSet.has(key) : false}
                    onChange={() => key && onToggle(key)}
                    aria-label={name}
                  />
                  <span className="broadcast-name-only">{name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const BroadcastNotification = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const productId = useMemo(() => productFilterId(selectedProduct), [selectedProduct]);
  const [includeWholesaler, setIncludeWholesaler] = useState(true);
  const [includeRetailer, setIncludeRetailer] = useState(true);

  const [wholesaleUsers, setWholesaleUsers] = useState([]);
  const [retailUsers, setRetailUsers] = useState([]);
  const [loadWholesale, setLoadWholesale] = useState(false);
  const [loadRetail, setLoadRetail] = useState(false);

  const [selectedWholesale, setSelectedWholesale] = useState(() => new Set());
  const [selectedRetail, setSelectedRetail] = useState(() => new Set());

  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3200);
  };

  const loadWholesaleList = includeWholesaler;
  const loadRetailList = includeRetailer;

  useEffect(() => {
    let cancelled = false;
    if (!loadWholesaleList) return undefined;
    (async () => {
      setLoadWholesale(true);
      try {
        const list = await fetchAllByUserType("wholeseller");
        if (!cancelled) setWholesaleUsers(list);
      } catch {
        if (!cancelled) {
          setWholesaleUsers([]);
          setToast({
            show: true,
            message: "Could not load wholesale users",
            type: "error",
          });
          setTimeout(() => setToast({ show: false, message: "", type: "" }), 3200);
        }
      } finally {
        if (!cancelled) setLoadWholesale(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadWholesaleList]);

  useEffect(() => {
    let cancelled = false;
    if (!loadRetailList) return undefined;
    (async () => {
      setLoadRetail(true);
      try {
        const list = await fetchAllByUserType("retailer");
        if (!cancelled) setRetailUsers(list);
      } catch {
        if (!cancelled) {
          setRetailUsers([]);
          setToast({
            show: true,
            message: "Could not load retail users",
            type: "error",
          });
          setTimeout(() => setToast({ show: false, message: "", type: "" }), 3200);
        }
      } finally {
        if (!cancelled) setLoadRetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRetailList]);

  const filteredWholesale = useMemo(
    () => wholesaleUsers.filter((u) => userMatchesProduct(u, productId)),
    [wholesaleUsers, productId]
  );

  const filteredRetail = useMemo(
    () => retailUsers.filter((u) => userMatchesProduct(u, productId)),
    [retailUsers, productId]
  );

  const visibleWholesaleKeys = useMemo(
    () => (includeWholesaler ? filteredWholesale.map(idKey).filter(Boolean) : []),
    [includeWholesaler, filteredWholesale]
  );
  const visibleRetailKeys = useMemo(
    () => (includeRetailer ? filteredRetail.map(idKey).filter(Boolean) : []),
    [includeRetailer, filteredRetail]
  );

  const allVisibleSelected = useMemo(() => {
    const hasWholesaleRows = includeWholesaler && visibleWholesaleKeys.length > 0;
    const hasRetailRows = includeRetailer && visibleRetailKeys.length > 0;
    if (!hasWholesaleRows && !hasRetailRows) return false;
    const wOk =
      !includeWholesaler ||
      (visibleWholesaleKeys.length > 0 &&
        visibleWholesaleKeys.every((k) => selectedWholesale.has(k)));
    const rOk =
      !includeRetailer ||
      (visibleRetailKeys.length > 0 && visibleRetailKeys.every((k) => selectedRetail.has(k)));
    return wOk && rOk;
  }, [
    includeWholesaler,
    includeRetailer,
    visibleWholesaleKeys,
    visibleRetailKeys,
    selectedWholesale,
    selectedRetail,
  ]);

  const toggleWholesaleId = useCallback((key) => {
    setSelectedWholesale((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleRetailId = useCallback((key) => {
    setSelectedRetail((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedWholesale((prev) => {
        const next = new Set(prev);
        visibleWholesaleKeys.forEach((k) => next.delete(k));
        return next;
      });
      setSelectedRetail((prev) => {
        const next = new Set(prev);
        visibleRetailKeys.forEach((k) => next.delete(k));
        return next;
      });
    } else {
      setSelectedWholesale((prev) => {
        const next = new Set(prev);
        visibleWholesaleKeys.forEach((k) => next.add(k));
        return next;
      });
      setSelectedRetail((prev) => {
        const next = new Set(prev);
        visibleRetailKeys.forEach((k) => next.add(k));
        return next;
      });
    }
  }, [allVisibleSelected, visibleWholesaleKeys, visibleRetailKeys]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const t = title.trim();
    const d = description.trim();
    if (!t) {
      showToast("Please enter a title", "error");
      return;
    }
    if (!d) {
      showToast("Please enter a message", "error");
      return;
    }

    if (!includeWholesaler && !includeRetailer) {
      showToast("Select at least Wholesaler and/or Retailer", "error");
      return;
    }

    if (includeWholesaler) {
      const n = wholesaleUsers
        .filter((u) => userMatchesProduct(u, productId))
        .filter((u) => idKey(u) && selectedWholesale.has(idKey(u))).length;
      if (n === 0) {
        showToast("Select at least one wholesale user, or uncheck Wholesale users", "error");
        return;
      }
    }
    if (includeRetailer) {
      const n = retailUsers
        .filter((u) => userMatchesProduct(u, productId))
        .filter((u) => idKey(u) && selectedRetail.has(idKey(u))).length;
      if (n === 0) {
        showToast("Select at least one retail user, or turn off Retailer users", "error");
        return;
      }
    }

    const users = collectUsersForBroadcast({
      includeWholesaler,
      includeRetailer,
      productId,
      wholesaleUsers,
      retailUsers,
      selectedWholesale,
      selectedRetail,
    });

    if (users.length === 0) {
      showToast("No users match your selection. Load user lists or adjust filters.", "error");
      return;
    }

    const payload = buildBroadcastPayload({
      title: t,
      message: d,
      users,
      product_id: productId.trim(),
    });

    try {
      setSending(true);
      const response = await sendBroadcastNotificationAPI(payload);
      if (isBroadcastSendSuccess(response)) {
        showToast(getBroadcastSuccessToastMessage(response), "success");
        setTitle("");
        setDescription("");
        setSelectedProduct(null);
        setSelectedWholesale(new Set());
        setSelectedRetail(new Set());
      } else if (response?.message) {
        showToast(getErrorMessage(response.message), "error");
      } else {
        showToast("Failed to send broadcast", "error");
      }
    } catch {
      showToast("Failed to send broadcast", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Toast toast={toast} />
      <div className="WholesaleUsers-layout BroadcastNotification-page">
        <Sidebar />
        <div className="WholesaleUsers-main-content broadcast-main-full">
          <form className="broadcast-page-form" onSubmit={handleSubmit}>
            <div className="broadcast-form-panel broadcast-form-panel--full">
              <div className="broadcast-form-section broadcast-form-section--fields broadcast-fields-stack">
                <label className="broadcast-field broadcast-field--stack">
                  <span className="broadcast-field-label broadcast-field-label--above">Title</span>
                  <input
                    type="text"
                    className="broadcast-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Notification title"
                    autoComplete="off"
                  />
                </label>

                <label className="broadcast-field broadcast-field--stack">
                  <span className="broadcast-field-label broadcast-field-label--above">Message</span>
                  <textarea
                    className="broadcast-textarea"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Message body shown to users"
                    rows={4}
                  />
                </label>

                <div className="broadcast-field broadcast-field--stack broadcast-product-field">
                  <ProductSearchSelect
                    optional
                    label="Product"
                    onlyActiveProducts
                    selectedProduct={selectedProduct}
                    onSelect={setSelectedProduct}
                    disabled={sending}
                  />
                </div>
              </div>

              <div className="broadcast-form-section broadcast-recipients-card">
                <div className="broadcast-recipients-card-head">
                  <span className="broadcast-field-label broadcast-field-label--above broadcast-recipients-heading">
                    Users
                  </span>
                </div>
                <div className="broadcast-user-type-tiles broadcast-user-type-tiles--three">
                  <label
                    className={`broadcast-check broadcast-check--tile ${
                      includeWholesaler ? "broadcast-check--tile-active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={includeWholesaler}
                      onChange={(e) => setIncludeWholesaler(e.target.checked)}
                    />
                    <span>Wholesale users</span>
                  </label>
                  <label
                    className={`broadcast-check broadcast-check--tile ${
                      includeRetailer ? "broadcast-check--tile-active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={includeRetailer}
                      onChange={(e) => setIncludeRetailer(e.target.checked)}
                    />
                    <span>Retailer users</span>
                  </label>
                  <label
                    className={`broadcast-check broadcast-check--tile broadcast-check--tile-selectall ${
                      allVisibleSelected ? "broadcast-check--tile-active" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={
                        !(includeWholesaler || includeRetailer) ||
                        (visibleWholesaleKeys.length === 0 && visibleRetailKeys.length === 0)
                      }
                      aria-label="Select or deselect all names shown below"
                    />
                    <span className="broadcast-check-label-selectall">Select all</span>
                  </label>
                </div>
                {(includeWholesaler || includeRetailer) && (
                  <>
                    {(includeWholesaler && loadWholesale) || (includeRetailer && loadRetail) ? (
                      <div className="loading-container broadcast-recipients-loading">
                        <div className="loading-spinner" />
                        <p>Loading…</p>
                      </div>
                    ) : (
                      <div className="broadcast-combined-grids broadcast-combined-grids--in-card">
                        {includeWholesaler ? (
                          <div className="broadcast-user-subsection">
                            <span className="broadcast-subsection-label">Wholesale users</span>
                            <NameGrid
                              users={filteredWholesale}
                              selectedSet={selectedWholesale}
                              onToggle={toggleWholesaleId}
                              emptyMessage="No wholesale users match."
                            />
                          </div>
                        ) : null}
                        {includeRetailer ? (
                          <div className="broadcast-user-subsection">
                            <span className="broadcast-subsection-label">Retail users</span>
                            <NameGrid
                              users={filteredRetail}
                              selectedSet={selectedRetail}
                              onToggle={toggleRetailId}
                              emptyMessage="No retail users match."
                            />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="broadcast-form-actions broadcast-form-actions--center">
                <button type="submit" className="insert-btn" disabled={sending}>
                  {sending ? "Submitting…" : "Submit"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default BroadcastNotification;
